// Minimal DPI extractor for JPEG (JFIF APP0 + EXIF APP1) and PNG (pHYs chunk).
// Returns `undefined` when no DPI metadata is present, so callers can fall back
// to a sensible default (72 DPI).

export async function readDpi(blob: Blob): Promise<number | undefined> {
  const head = new Uint8Array(await blob.slice(0, 65536).arrayBuffer());
  if (head.length < 4) return undefined;

  // PNG signature: 89 50 4E 47 0D 0A 1A 0A
  if (
    head[0] === 0x89 &&
    head[1] === 0x50 &&
    head[2] === 0x4e &&
    head[3] === 0x47
  ) {
    return readPngDpi(head);
  }

  // JPEG signature: FF D8
  if (head[0] === 0xff && head[1] === 0xd8) {
    return readJpegDpi(head);
  }

  return undefined;
}

function readPngDpi(bytes: Uint8Array): number | undefined {
  // Walk chunks after the 8-byte signature looking for pHYs (or IHDR/IDAT to
  // stop early).
  let offset = 8;
  while (offset + 8 <= bytes.length) {
    const len = readUInt32BE(bytes, offset);
    const type = String.fromCharCode(
      bytes[offset + 4] ?? 0,
      bytes[offset + 5] ?? 0,
      bytes[offset + 6] ?? 0,
      bytes[offset + 7] ?? 0,
    );
    const dataStart = offset + 8;
    if (type === 'pHYs' && len >= 9) {
      const ppuX = readUInt32BE(bytes, dataStart);
      const unit = bytes[dataStart + 8];
      if (unit === 1) {
        // pixels per meter → dots per inch.
        return Math.round(ppuX * 0.0254);
      }
      return undefined;
    }
    if (type === 'IDAT' || type === 'IEND') return undefined;
    offset = dataStart + len + 4; // skip data + CRC
  }
  return undefined;
}

function readJpegDpi(bytes: Uint8Array): number | undefined {
  // Walk JPEG segments looking for JFIF APP0 (FF E0) or EXIF APP1 (FF E1).
  let offset = 2;
  while (offset + 4 < bytes.length) {
    if (bytes[offset] !== 0xff) return undefined;
    const marker = bytes[offset + 1];
    const segLen = readUInt16BE(bytes, offset + 2);
    if (marker === 0xe0) {
      // JFIF
      const id = String.fromCharCode(
        bytes[offset + 4] ?? 0,
        bytes[offset + 5] ?? 0,
        bytes[offset + 6] ?? 0,
        bytes[offset + 7] ?? 0,
      );
      if (id === 'JFIF') {
        const units = bytes[offset + 9]; // 0 = no units, 1 = inches, 2 = cm
        const xDensity = readUInt16BE(bytes, offset + 10);
        if (units === 1) return xDensity;
        if (units === 2) return Math.round(xDensity * 2.54);
      }
    } else if (marker === 0xe1) {
      // EXIF APP1
      const id = String.fromCharCode(
        bytes[offset + 4] ?? 0,
        bytes[offset + 5] ?? 0,
        bytes[offset + 6] ?? 0,
        bytes[offset + 7] ?? 0,
      );
      if (id === 'Exif') {
        const exif = readExifDpi(bytes.subarray(offset + 10, offset + 2 + segLen));
        if (exif !== undefined) return exif;
      }
    } else if (marker === 0xda || marker === 0xd9) {
      // Start of scan / end — done.
      return undefined;
    }
    offset += 2 + segLen;
  }
  return undefined;
}

function readExifDpi(tiff: Uint8Array): number | undefined {
  if (tiff.length < 8) return undefined;
  const little = tiff[0] === 0x49 && tiff[1] === 0x49;
  const big = tiff[0] === 0x4d && tiff[1] === 0x4d;
  if (!little && !big) return undefined;
  const read16 = (o: number) =>
    little ? readUInt16LE(tiff, o) : readUInt16BE(tiff, o);
  const read32 = (o: number) =>
    little ? readUInt32LE(tiff, o) : readUInt32BE(tiff, o);

  const ifd0Offset = read32(4);
  if (ifd0Offset >= tiff.length) return undefined;
  const numEntries = read16(ifd0Offset);

  let xResolution: { num: number; den: number } | undefined;
  let resolutionUnit: number | undefined;

  for (let i = 0; i < numEntries; i++) {
    const entryOffset = ifd0Offset + 2 + i * 12;
    if (entryOffset + 12 > tiff.length) break;
    const tag = read16(entryOffset);
    const valueOffset = read32(entryOffset + 8);
    if (tag === 0x011a) {
      // XResolution — RATIONAL
      if (valueOffset + 8 <= tiff.length) {
        xResolution = {
          num: read32(valueOffset),
          den: read32(valueOffset + 4),
        };
      }
    } else if (tag === 0x0128) {
      // ResolutionUnit — SHORT, value packed into first 2 bytes of valueOffset
      resolutionUnit = read16(entryOffset + 8);
    }
  }

  if (!xResolution || xResolution.den === 0) return undefined;
  const dpi = xResolution.num / xResolution.den;
  if (resolutionUnit === 3) return Math.round(dpi * 2.54); // cm → in
  return Math.round(dpi);
}

function readUInt16BE(b: Uint8Array, o: number): number {
  return ((b[o] ?? 0) << 8) | (b[o + 1] ?? 0);
}
function readUInt16LE(b: Uint8Array, o: number): number {
  return ((b[o + 1] ?? 0) << 8) | (b[o] ?? 0);
}
function readUInt32BE(b: Uint8Array, o: number): number {
  return (
    ((b[o] ?? 0) * 0x1000000) +
    (((b[o + 1] ?? 0) << 16) | ((b[o + 2] ?? 0) << 8) | (b[o + 3] ?? 0))
  );
}
function readUInt32LE(b: Uint8Array, o: number): number {
  return (
    ((b[o + 3] ?? 0) * 0x1000000) +
    (((b[o + 2] ?? 0) << 16) | ((b[o + 1] ?? 0) << 8) | (b[o] ?? 0))
  );
}
